@use "Styles/includes/variables";
@use "Styles/mixins";

// Full strength while more content is below, off at the very end.
@keyframes modal-scroll-shadow {
    from,
    99% {
        @include mixins.overlay-footer-shadow;
    }
    to {
        box-shadow: none;
    }
}

@keyframes modal-slide-in {
    from {
        opacity: 0;
        transform: translateY(-16px) scale(0.97);
    }
    to {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
}

@keyframes modal-slide-out {
    from {
        opacity: 1;
        transform: translateY(0) scale(1);
    }
    to {
        opacity: 0;
        transform: translateY(-16px) scale(0.97);
    }
}

:host {
    display: contents;
}

:host([variant="lightbox"]) {
    .vm-modal {
        width: 100vw;
        height: 100vh;
        height: 100dvh;
        max-width: 100vw;
        max-height: 100vh;
        max-height: 100dvh;
        overflow: hidden;
        color: mixins.get-color(text, text-white);
        background: transparent;
        border-radius: 0;
        box-shadow: none;

        @include mixins.desktop-and-up {
            width: fit-content;
            height: fit-content;
            max-width: 90vw;
            max-height: 90vh;
            max-height: 90dvh;
        }

        &__container {
            padding: 0;
            max-height: none;
            position: relative;
            // Keep controls in the upper half of even a small stage; both layers
            // contribute to its intrinsic size instead of overflowing a short dialog.
            display: grid;
            grid-template-rows: repeat(2, 1fr);
            height: 100%;

            @include mixins.desktop-and-up {
                height: auto;
            }
        }

        &__header {
            grid-area: 1 / 1;
            align-self: start;
            justify-self: end;
            height: auto;
            padding: max(16px, env(safe-area-inset-top)) max(16px, env(safe-area-inset-right)) 16px 16px;
            z-index: 1;
        }

        // Only the close button floats over the stage. The hidden header still names
        // the dialog: aria-labelledby reads hidden content.
        &__header-content {
            display: none;
        }

        &__close-btn {
            padding: 8px;
            background: mixins.get-color(background, bg-white-alpha70);
            border-radius: 100px;
            color: mixins.get-color(text, text-black);
        }

        &__body {
            padding: 0;
            grid-row: 1 / -1;
            grid-column: 1;
            overflow: hidden;
            display: grid;
            place-items: center;
            position: relative;
        }

        &__footer {
            display: none;
        }

        &::backdrop {
            background: mixins.get-color(background, bg-overlay);

            @include mixins.desktop-and-up {
                background: rgba(mixins.get-color(background, bg-gray-900), 0.8);
            }
        }
    }
}

.vm-modal {
    border: none;
    border-radius: 24px;
    padding: 0;
    max-width: 650px;
    width: calc(100% - 32px);
    max-height: calc(100dvh - 64px);
    overflow: var(--vm-modal-overflow, auto);
    background: mixins.get-color(bg, solid, primary);
    box-shadow:
        -2px -12px 48px -12px rgba(10, 13, 18, 0.18),
        4px -8px 4px -2px rgba(10, 13, 18, 0.04);
    animation: modal-slide-in 0.2s ease-out;

    &::backdrop {
        @include mixins.overlay-backdrop;
    }

    &--closing {
        animation: modal-slide-out 0.2s ease-in forwards;

        &::backdrop {
            @include mixins.overlay-backdrop-closing;
        }
    }

    &__container {
        // Lifted so the footer, a sibling of the scroller, can reference it.
        timeline-scope: --modal-body;
        display: flex;
        flex-direction: column;
        max-height: calc(100dvh - 64px);
        padding: 8px 0 24px;

        // When a footer is slotted in, drop the container's bottom padding so the
        // footer sits flush against the dialog's bottom edge.
        &:has(.vm-modal__footer:not(.vm-modal__footer--empty)) {
            padding-bottom: 0;
        }
    }

    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        height: 24px;
        padding: 8px 16px;
        flex-shrink: 0;
    }

    &__header-content {
        flex: 1;
        min-width: 0;
    }

    &__close-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        background: none;
        border: none;
        padding: 4px;
        cursor: pointer;
        border-radius: 4px;
        color: mixins.get-color(text, text-tertiary);
        flex-shrink: 0;
    }

    &__body {
        padding: 16px 32px;
        overflow-y: auto;
        flex: 1;
        min-height: 0;
        scroll-timeline: --modal-body block;
    }

    &__title {
        margin: 0 0 24px;
        font-family: variables.$fontQuaternary;
        font-size: 20px;
        line-height: 30px;
        font-weight: 900;
        color: mixins.get-color(text, text-primary);
    }

    // Sticky footer area pinned below the scrollable body. Hidden when nothing is
    // slotted in (driven by slotchange in vm-modal.ts), so consumers that don't use
    // the footer get no visual change.
    &__footer {
        // Positioned so content in the body can't paint over the shadow.
        position: relative;
        flex-shrink: 0;
        padding: 16px 32px;
        background: mixins.get-color(bg, solid, primary);

        // No support - always-on shadow.
        @supports not (timeline-scope: --modal-body) {
            @include mixins.overlay-footer-shadow;
        }

        // Support - shadow only while the body has more to scroll.
        @supports (timeline-scope: --modal-body) {
            animation: modal-scroll-shadow linear both;
            animation-timeline: --modal-body;
        }

        &--empty {
            display: none;
        }
    }
}
